import UpgradeAccountButton, {
  UpgradeAccountButtonProps,
} from '@/components/buttons/UpgradeAccountButton';
import { ROLE_ACCESS } from '@/constant/storage';
import {
  generateCourseMaterialUrl,
  generateTrialLearningLink,
} from '@/helpers/generatePathname';
import {
  CourseMaterialEnum,
  FolderStatusEnum,
  ICourseDetailResponse,
} from '@/interfaces/training.interface';
import { checkIsUserJoined } from 'common/checkRoleUser';
import cookieCommons from 'common/cookieCommons';
import {
  checkSubscriptionResource,
  hasUserSubscriptedIdealPlan,
} from 'common/subscription/checkSubscriptionResource';
import { Button, ButtonProps } from 'design-system/components/Button';
import { useRouter } from 'next/router';
import { DetailedHTMLProps, HTMLAttributes, useContext } from 'react';
import { UseQueryResult } from 'react-query';
import { CourseDetailPermissionContext } from 'state/course/CourseDetailPermissionProvider';
import { twMerge } from 'tailwind-merge';
import RequiredJoinGroupHOC from '../lesson/RequiredJoinGroupHOC';
import PurchaseButton from './button/PurchaseButton';

enum EButtonType {
  Request = 'Request',
  Purchase = 'Purchase',
  Learn = 'Learn',
}

type CourseActionsProps = {
  buttonActionsProps?: {
    [keyof in EButtonType]?: ButtonProps & {
      wrapperProps?: DetailedHTMLProps<
        HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
      >;
      upgradeButtonProps?: UpgradeAccountButtonProps;
    };
  };
  showTrialButton?: boolean;
};

const CourseActions = ({
  buttonActionsProps,
  showTrialButton = true,
}: CourseActionsProps) => {
  const {
    courseDetailQueryReturn,
    mineRole: role,
    fullyLock,
    eachLock,
    latestSubscription,
  } = useContext(CourseDetailPermissionContext);

  const router = useRouter();
  const { cid } = router.query;

  const { data: course } = courseDetailQueryReturn as UseQueryResult<
    ICourseDetailResponse,
    unknown
  >;

  const isPrivateGroup = course?.group?.status === FolderStatusEnum.Private;

  const { coursePrice, requiresVerified, userOrder, courseMaterials } =
    course || {};
  const { Learn, Purchase, Request } = buttonActionsProps || {};
  const isVisitor = !cookieCommons.getAccessToken();
  const isCompleted = course?.userProgress?.isCompleted;

  const userHasPaid =
    userOrder?.hasUserPaid ||
    checkSubscriptionResource(latestSubscription).course.accessToCourse;
  const isOwnerRole = role === ROLE_ACCESS.Owner;
  const isPaidCourse = coursePrice?.isPaidCourse;
  const materialSelected =
    courseMaterials?.find((lesson) => !lesson.isCompleted) ||
    courseMaterials?.[0];

  const buttonType: EButtonType = (() => {
    if (isOwnerRole) return EButtonType.Learn;

    if (isVisitor) {
      if (isPrivateGroup) return EButtonType.Request;
      if (isPaidCourse) return EButtonType.Purchase;
      if (!isPaidCourse) return EButtonType.Learn;
    }

    if (!isPrivateGroup && isPaidCourse && !userHasPaid)
      return EButtonType.Purchase;

    if (!checkIsUserJoined(role)) return EButtonType.Request;

    if (isPaidCourse && !userHasPaid && !isOwnerRole)
      return EButtonType.Purchase;

    return EButtonType.Learn;
  })();

  const mapButton = {
    [EButtonType.Learn]: (
      <Button
        {...Learn}
        className={`w-full ${
          (isCompleted || userHasPaid) &&
          'bg-Success/Main hover:bg-Success/Hover active:bg-Success/Pressed'
        } ${Learn?.className}`}
        emphasis="high"
        startIcon={{
          name: 'play',
        }}
      >
        Học ngay
      </Button>
    ),
    [EButtonType.Purchase]: (
      <div
        {...Purchase?.wrapperProps}
        className={twMerge(
          'flex flex-col gap-2 lg:block lg:space-y-3',
          Purchase?.wrapperProps?.className
        )}
      >
        <PurchaseButton
          isRequiresVerified={requiresVerified || false}
          buttonProps={Purchase}
        />
        {!hasUserSubscriptedIdealPlan(latestSubscription) && (
          <UpgradeAccountButton
            {...Purchase?.upgradeButtonProps}
            ignoreMobile={Purchase?.upgradeButtonProps?.ignoreMobile ?? true}
            className={twMerge(
              'w-full',
              Purchase?.upgradeButtonProps?.className
            )}
            disableChangeWhenPackageActive
          >
            Tiết kiệm hơn với gói Premium
          </UpgradeAccountButton>
        )}
      </div>
    ),
    [EButtonType.Request]: (
      <Button
        emphasis="high"
        {...Request}
        className={`w-full ${Request?.className}`}
      >
        {!isPaidCourse && !isPrivateGroup
          ? 'Bắt đầu ngay'
          : role === ROLE_ACCESS.Requested
          ? 'Đã gửi yêu cầu'
          : 'Tham gia nhóm'}
      </Button>
    ),
  };

  return (
    <div className="space-y-[12px]">
      {buttonType === EButtonType.Purchase ? (
        mapButton[buttonType]
      ) : (
        <>
          <RequiredJoinGroupHOC
            href={generateCourseMaterialUrl({
              materialType:
                materialSelected?.materialType || CourseMaterialEnum.Lesson,
              materialId: materialSelected?.id || '',
              courseId: course?.id || '',
            })}
            isRequiresVerified={requiresVerified || false}
          >
            {mapButton[buttonType]}
          </RequiredJoinGroupHOC>
        </>
      )}

      {(fullyLock || eachLock) &&
        course?.allowTrial &&
        showTrialButton &&
        course.courseMaterials.length > 0 && (
          <RequiredJoinGroupHOC
            className="flex-shrink-0"
            href={generateTrialLearningLink({
              courseId: cid as string,
              materialId: course.courseMaterials[0]?.id,
              materialType: course.courseMaterials[0]?.materialType,
            })}
          >
            <Button emphasis="medium" className={`w-full`}>
              Học thử ngay
            </Button>
          </RequiredJoinGroupHOC>
        )}
    </div>
  );
};

export default CourseActions;
